import axios from "./axiosConfig";
import { getUploadUrl, uploadToS3 } from "./filesService";

// Crear nuevo reporte de plagas
async function createPestReport(reportData) {
  try {
    const { eventId, serviceId, pestLevel, entryTime, exitTime, reportPdf } = reportData;

    // Validaciones básicas
    if (!eventId) throw new Error("eventId es requerido");
    if (!serviceId) throw new Error("serviceId es requerido");
    if (pestLevel === undefined || pestLevel === null)
      throw new Error("pestLevel es requerido");
    if (!entryTime) throw new Error("entryTime es requerido");
    if (!exitTime) throw new Error("exitTime es requerido");
    if (!reportPdf) throw new Error("reportPdf es requerido");

    // Validar pestLevel en rango
    const level = parseFloat(pestLevel);
    if (isNaN(level) || level < 0 || level > 100) {
      throw new Error("pestLevel debe estar entre 0 y 100");
    }

    // Validar tiempos
    const entry = new Date(entryTime);
    const exit = new Date(exitTime);

    if (isNaN(entry.getTime())) throw new Error("entryTime es inválido");
    if (isNaN(exit.getTime())) throw new Error("exitTime es inválido");
    if (exit <= entry) throw new Error("exitTime debe ser posterior a entryTime");

    // 1. Obtener datos del servicio usando su ID para construir ruta de fecha
    const serviceResponse = await axios.get(`/services/${serviceId}`);
    const serviceData = serviceResponse.data.data;

    // Construir la ruta de fecha basada en la fecha del servicio
    const serviceDate = new Date(serviceData.date || serviceData.serviceDate || serviceData.createdAt);
    const year = serviceDate.getUTCFullYear();
    const month = String(serviceDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(serviceDate.getUTCDate()).padStart(2, '0');
    const dateFolder = `soportes/${year}/${month}/${day}/`;

    // 2. Obtener URL prefirmada para S3 con subfolder de fecha
    const { uploadUrl, key } = await getUploadUrl(reportPdf, dateFolder);

    // 3. Subir PDF a S3
    await uploadToS3(uploadUrl, reportPdf);

    // 4. Construir URL del PDF en S3
    const reportPdfUrl = `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/${key}`;

    // 5. Enviar datos a MongoDB
    const response = await axios.post("/reports/pest", {
      eventId,
      pestLevel: level,
      entryTime: entry.toISOString(),
      exitTime: exit.toISOString(),
      reportPdfUrl,
      reportPdfKey: key,
    });

    return {
      success: true,
      report: response.data.data,
      message: response.data.message,
    };
  } catch (err) {
    console.error("Error al crear reporte de plagas:", err);
    throw new Error(
      err.response?.data?.message ||
        err.message ||
        "Error al crear el reporte de plagas"
    );
  }
}

// Obtener todos los reportes
async function getAllReports(filters = {}) {
  try {
    const response = await axios.get("/reports/pest", { params: filters });
    return response.data.data || [];
  } catch (err) {
    console.error("Error al obtener reportes:", err);
    throw err;
  }
}

// Obtener reporte por ID
async function getReportById(reportId) {
  try {
    const response = await axios.get(`/reports/pest/${reportId}`);
    return response.data.data;
  } catch (err) {
    console.error("Error al obtener reporte:", err);
    throw err;
  }
}

// Obtener reportes de un evento
async function getReportsByEventId(eventId) {
  try {
    const response = await axios.get(`/reports/pest/event/${eventId}`);
    return response.data.data || [];
  } catch (err) {
    console.error("Error al obtener reportes del evento:", err);
    throw err;
  }
}

// Actualizar reporte
async function updateReport(reportId, updateData) {
  try {
    const response = await axios.put(`/reports/pest/${reportId}`, updateData);
    return response.data.data;
  } catch (err) {
    console.error("Error al actualizar reporte:", err);
    throw err;
  }
}

// Eliminar reporte
async function deleteReport(reportId) {
  try {
    await axios.delete(`/reports/pest/${reportId}`);
    return { success: true, message: "Reporte eliminado exitosamente" };
  } catch (err) {
    console.error("Error al eliminar reporte:", err);
    throw err;
  }
}

// Obtener estadísticas
async function getStatistics(filters = {}) {
  try {
    const response = await axios.get("/reports/pest/statistics", {
      params: filters,
    });
    return response.data.data;
  } catch (err) {
    console.error("Error al obtener estadísticas:", err);
    throw err;
  }
}

async function getHotBranches() {
  try {
    const response = await axios.get("/reports/pest/hot-branches");
    return response.data.data || [];
  } catch (err) {
    console.error("Error al obtener clientes con sucursales infestadas:", err);
    throw err;
  }
}

// Convertir archivo PDF a base64
function pdfToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result.split(",")[1] || reader.result;
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Descargar PDF desde S3
async function downloadReportPdf(pdfUrl, fileName = "reporte-plagas.pdf") {
  try {
    const response = await axios.get(pdfUrl, { responseType: "blob" });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (err) {
    console.error("Error al descargar PDF:", err);
    throw err;
  }
}

const pestReportService = {
  createPestReport,
  getAllReports,
  getReportById,
  getReportsByEventId,
  updateReport,
  deleteReport,
  getStatistics,
  getHotBranches,
  pdfToBase64,
  downloadReportPdf,
};

export default pestReportService;
